/**
 * Component Registry
 * Centralized metadata for all landing page component types
 */

import type { LandingPageComponentType, LandingPageComponent } from "./types";

export interface ComponentMetadata {
  type: LandingPageComponentType;
  label: string;
  maxInstances: number; // 1 for single-use, Infinity for multi-use
}

export const COMPONENT_REGISTRY: Record<LandingPageComponentType, ComponentMetadata> = {
  navbar: {
    type: "navbar",
    label: "Navbar",
    maxInstances: 1,
  },
  hero: {
    type: "hero",
    label: "Hero",
    maxInstances: 1,
  },
  schedule: {
    type: "schedule",
    label: "Schedule",
    maxInstances: 1,
  },
  faq: {
    type: "faq",
    label: "FAQ",
    maxInstances: 1,
  },
  venue: {
    type: "venue",
    label: "Venue",
    maxInstances: 1,
  },
  carousel: {
    type: "carousel",
    label: "Carousel",
    maxInstances: Infinity,
  },
  textButton: {
    type: "textButton",
    label: "Text & Button",
    maxInstances: Infinity,
  },
  richText: {
    type: "richText",
    label: "Rich Text",
    maxInstances: Infinity,
  },
  activities: {
    type: "activities",
    label: "Activities",
    maxInstances: Infinity,
  },
};

// Map component types to their anchor IDs
export const COMPONENT_ANCHORS: Partial<Record<LandingPageComponentType, string>> = {
  navbar: "navbar",
  hero: "hero",
  schedule: "schedule",
  faq: "faq",
  venue: "venue",
  activities: "activities",
  // carousel: no anchor - not referenced in navigation
  // richText: dynamic anchor based on title
};

/**
 * Helper function to generate anchor ID from text
 */
export const generateAnchorId = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
};

/**
 * Helper function to get component anchor display with label
 * For static components, uses COMPONENT_ANCHORS
 * For dynamic components (like richText), extracts from component config
 */
export const getComponentAnchor = (
  type: LandingPageComponentType,
  component?: LandingPageComponent
): { anchor: string; label: string } | null => {
  // Handle richText specially - get anchor from component navigationLabel
  if (type === "richText" && component) {
    const navigationLabel = component.config?.navigationLabel;
    console.log("getComponentAnchor for richText:", { navigationLabel, config: component.config });
    if (!navigationLabel) return null; // Skip if no navigation label

    const anchor = generateAnchorId(navigationLabel);
    const result = {
      anchor,
      label: navigationLabel,
    };
    console.log("getComponentAnchor result for richText:", result);
    return result;
  }

  // Handle static anchors
  const anchor = COMPONENT_ANCHORS[type];
  if (!anchor) return null;

  const metadata = COMPONENT_REGISTRY[type];
  return {
    anchor,
    label: metadata?.label || type,
  };
};

/**
 * Get component anchor display string for UI
 */
export const getComponentAnchorDisplay = (
  type: LandingPageComponentType,
  component?: LandingPageComponent
): string | null => {
  const anchorData = getComponentAnchor(type, component);
  return anchorData ? `#${anchorData.anchor}` : null;
};
